<template>
  <v-container
    class="distr-spec-percentage-alt-container standard-content-container"
  >
    <v-row no-gutters>
      <v-col cols="12" md="5">
        <v-row no-gutters class="title-section">
          <v-col
            cols="12"
            sm="2"
            class="link-container"
            :class="{
              'small-container':
                this.$vuetify.breakpoint.width <=
                this.$vuetify.breakpoint.thresholds.xs,
            }"
          >
            <router-link
              :to="{ name: backRouteName, params: { id: backRouteId } }"
              class="back-arrow"
              ><v-icon large color="mid-grey" class="mt-1">{{
                icons.mdiArrowLeft
              }}</v-icon></router-link
            >
          </v-col>
          <v-col cols="12" sm="10" class="standard-title-container">
            <h1>Remainder of Estate</h1>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="7">
        <div class="left-section-container">
          <h2>
            If
            <span class="person-remainder-percentage">{{
              specificPercentage.beneficiaryDisplayName
            }}</span>
            passes on before you, how would you like the part you left to them
            to be distributed?
          </h2>
          <v-col cols="12" class="precentage-items-container">
            <AssignSpecificPercentages
              :key="specificPercentage.id"
              :testator="testator"
              :parent-beneficiary-id="parentBeneficiaryId"
              :existing-specific-percentages="
                existingSpecificPercentageAlternativeBeneficiaries
              "
              :button-loading="buttonLoading"
              @submit="saveSpecificPercentages"
            />
          </v-col>
        </div>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
  </v-container>
</template>

<script>
  import { UPDATE_DISTRIBUTION_SPECIFIC_PERCENTAGE_MUTATION } from '../../constants/graphql/remainder-of-estates';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import AssignSpecificPercentages from './AssignSpecificPercentages';

  export default {
    name: 'DistributionSpecificPercentageAlternatives',
    components: {
      AssignSpecificPercentages,
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        alternativeDistribution: null,
        showPersonDialog: false,
        showCharityDialog: false,
        showTrustDialog: false,
        alternativePerson: {},
        alternativeCharity: {},
        alternativeTrust: {},
        buttonLoading: false,
        errorMessage: null,
        specificPercentagePeople: [],
      };
    },
    computed: {
      backRouteName() {
        if (parseInt(this.$route.params['id']) === 1) {
          return 'DistributionSpecificPercentages';
        } else {
          return 'DistributionSpecificPercentageAlternatives';
        }
      },
      backRouteId() {
        if (parseInt(this.$route.params['id']) === 1) {
          return '';
        } else {
          return parseInt(this.$route.params['id'] - 1);
        }
      },
      windowLocation() {
        return window.location.href;
      },
      currentIndex() {
        return parseInt(this.$route.params.id) - 1;
      },
      specificPercentage() {
        return this.specificPercentagePeople[this.currentIndex];
      },
      existingSpecificPercentageAlternativeBeneficiaries() {
        return (
          this.specificPercentage
            ?.distributionSpecificPercentageAlternativeBeneficiaries || []
        );
      },
      parentBeneficiaryId() {
        return this.specificPercentage?.beneficiaryId;
      },
    },
    watch: {
      '$route.params.id'() {
        this.loadSpecificPercentagePeople();
        this.setData();
      },
    },
    created() {
      this.loadSpecificPercentagePeople();
      if (!this.testator.remainderOfEstate || !this.specificPercentage) {
        this.$router.push('/remainder-of-estate/distribution');
        return;
      }
      this.setData();
    },
    methods: {
      loadSpecificPercentagePeople() {
        const specificPercentages =
          this.testator.remainderOfEstate?.distributionSpecificPercentages ||
          [];
        this.specificPercentagePeople = JSON.parse(
          JSON.stringify(
            specificPercentages.filter(sp => sp.beneficiaryType == 'Person')
          )
        );
      },
      saveSpecificPercentages({ specificPercentages, deletedRecordIds }) {
        if (this.buttonLoading) return;
        this.buttonLoading = true;
        const distributionSpecificPercentageInputDetails =
          this.specificPercentageInputForMutation({
            specificPercentages,
            deletedRecordIds,
          });
        const id = this.specificPercentage.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_DISTRIBUTION_SPECIFIC_PERCENTAGE_MUTATION,
            variables: {
              id,
              distributionSpecificPercentageInputDetails,
            },
          })
          .then(_response => {
            this.redirectToNext();
            this.buttonLoading = false;
          })
          .catch(error => {
            this.errorMessage = this.serverErrorMessage(error);
            this.displayError();
            throw error;
          });
      },
      serverErrorMessage(error) {
        const graphQLErrors = error?.graphQLErrors || [];
        return graphQLErrors.length ? graphQLErrors[0].message : null;
      },
      specificPercentageInputForMutation({
        specificPercentages,
        deletedRecordIds,
      }) {
        var input = {};

        input.distributionSpecificPercentageAlternativeBeneficiaries =
          specificPercentages.map(obj => ({
            id: obj.id,
            distributionSpecificPercentageId:
              obj.distributionSpecificPercentageId,
            beneficiaryType: obj.beneficiaryType,
            beneficiaryId: obj.beneficiaryId,
            percentage: parseInt(obj.percentage),
            deletedRecordIds: deletedRecordIds,
          }));

        return input;
      },
      redirectToNext() {
        const nextId = parseInt(this.$route.params.id) + 1;

        if (nextId > this.specificPercentagePeople.length) {
          this.$router.push({ name: 'FamilyObliteration' });
        } else {
          this.$router.push(
            '/remainder-of-estate/distribution-specific-percentage-alternative/' +
              nextId
          );
        }
      },
      setData() {
        this.alternativeDistribution =
          this.specificPercentage.alternativeDistribution;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .distr-spec-percentage-alt-container {
    padding-top: 28px;
    padding-bottom: 48px;

    .left-section-container {
      h2 {
        line-height: 28px;
        font-weight: 700;
      }
    }

    .remainder-title {
      line-height: 36px;
    }

    .next-button {
      margin-top: 30px;
    }

    .precentage-items-container {
      padding-left: 0;
      padding-right: 0;
    }

    .person-remainder-percentage {
      // border and background color in global.scss because of IE issue
      padding-left: 8px;
      padding-right: 8px;
      border-radius: 4px;
      margin-left: 2px;
      margin-right: 2px;
    }
  }
  .distr-spec-percentage-alt-container.small-distr-container {
    padding: 0;
    padding-top: 28px;
  }
</style>
